import { ref } from 'vue'

const DEFAULT_LAT = 31.945604
const DEFAULT_LNG = 35.960444
const CACHE_KEY = 'dhikr_last_location'
const CACHE_MAX_AGE_MS = 10 * 60 * 1000
const RETRY_BACKOFF_MS = 5 * 60 * 1000

let inFlightLocate: Promise<void> | null = null
let lastLocateFailureAt = 0

type CachedLocation = {
  lat: number
  lng: number
  ts: number
}

function readCachedLocation(): CachedLocation | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedLocation
    if (typeof parsed.lat !== 'number' || typeof parsed.lng !== 'number' || typeof parsed.ts !== 'number') {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeCachedLocation(lat: number, lng: number) {
  const payload: CachedLocation = { lat, lng, ts: Date.now() }
  localStorage.setItem(CACHE_KEY, JSON.stringify(payload))
}

export function useGeolocation() {
  const cached = readCachedLocation()
  const latitude = ref(cached?.lat ?? DEFAULT_LAT)
  const longitude = ref(cached?.lng ?? DEFAULT_LNG)
  const located = ref(false)
  const error = ref('')

  function locate(force = false): Promise<void> {
    if (!force && inFlightLocate) return inFlightLocate

    inFlightLocate = new Promise((resolve) => {
      if (!navigator.geolocation) {
        error.value = 'Geolocation is not supported'
        resolve()
        return
      }

      const now = Date.now()
      const cachedLoc = readCachedLocation()
      const hasFreshCache = cachedLoc && now - cachedLoc.ts <= CACHE_MAX_AGE_MS
      if (!force && hasFreshCache) {
        latitude.value = cachedLoc.lat
        longitude.value = cachedLoc.lng
        located.value = true
        error.value = ''
        resolve()
        return
      }

      if (!force && lastLocateFailureAt > 0 && now - lastLocateFailureAt < RETRY_BACKOFF_MS) {
        resolve()
        return
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          latitude.value = pos.coords.latitude
          longitude.value = pos.coords.longitude
          located.value = true
          error.value = ''
          writeCachedLocation(pos.coords.latitude, pos.coords.longitude)
          lastLocateFailureAt = 0
          resolve()
        },
        (geoError) => {
          located.value = false
          lastLocateFailureAt = Date.now()
          error.value = geoError.code === geoError.PERMISSION_DENIED
            ? 'Location access denied, using default'
            : 'Unable to determine location, using default'
          resolve()
        },
        { enableHighAccuracy: true, timeout: 8000 },
      )
    })

    return inFlightLocate.finally(() => {
      inFlightLocate = null
    })
  }

  return { latitude, longitude, located, error, locate }
}
